import { useMemo } from 'react'
import { useActivityLog, type LogEntry } from '../ui/ActivityLog'

// Thin client for our same-origin /api/*. It unwraps the { data, log } envelope,
// pushes any log entries into the Activity panel, and throws on non-2xx with the
// server's error message. Every module talks to its backend through this.

interface Envelope<T> { data?: T; log?: LogEntry[]; error?: string }

export interface Api {
  // quiet skips pushing log entries to the Activity panel (for background polls)
  get<T>(path: string, opts?: { quiet?: boolean }): Promise<T>
  post<T>(path: string, body?: unknown): Promise<T>
  put<T>(path: string, body?: unknown): Promise<T>
  del<T>(path: string): Promise<T>
  // POST that consumes an NDJSON stream: {type:'log',entry} lines push to the
  // Activity panel live; resolves with the final {type:'done',data}.
  streamPost<T>(path: string, body?: unknown): Promise<T>
}

export function useApi(): Api {
  const { push } = useActivityLog()

  // Memoised on `push` (which is stable) so `api` keeps a stable identity across
  // renders. Without this, every consumer that derives a useCallback/effect from
  // `api` re-runs each render — e.g. useTokens' refresh effect would loop and
  // hammer /api/tokens, and the registration poll interval would be recreated
  // on every render.
  return useMemo<Api>(() => {
    async function call<T>(method: string, path: string, body?: unknown, quiet = false): Promise<T> {
    const res = await fetch(`/api${path}`, {
      method,
      headers: body != null ? { 'content-type': 'application/json' } : undefined,
      body: body != null ? JSON.stringify(body) : undefined,
    })
    let json: Envelope<T> & Record<string, unknown>
    try {
      json = await res.json()
    } catch {
      throw new Error(`HTTP ${res.status}`)
    }
    if (json.log && !quiet) push(json.log)
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
    // success envelope has { data }; bare endpoints (/me) return the object itself
    return (json.data !== undefined ? json.data : (json as unknown)) as T
  }

  async function streamPost<T>(path: string, body?: unknown): Promise<T> {
    const res = await fetch(`/api${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: body != null ? JSON.stringify(body) : undefined,
    })
    if (!res.ok || !res.body) {
      let msg = `HTTP ${res.status}`
      try {
        const j = (await res.json()) as { error?: string; log?: LogEntry[] }
        if (j.log) push(j.log)
        if (j.error) msg = j.error
      } catch { /* non-JSON error */ }
      throw new Error(msg)
    }

    const reader = res.body.getReader()
    const dec = new TextDecoder()
    let buf = ''
    let result: T | undefined
    let errMsg: string | undefined

    const handle = (line: string) => {
      const s = line.trim()
      if (!s) return
      let ev: { type?: string; entry?: LogEntry; data?: T; error?: string }
      try { ev = JSON.parse(s) } catch { return }
      if (ev.type === 'log' && ev.entry) push([ev.entry])
      else if (ev.type === 'done') result = ev.data
      else if (ev.type === 'error') errMsg = ev.error
    }

    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += dec.decode(value, { stream: true })
      let nl: number
      while ((nl = buf.indexOf('\n')) >= 0) {
        handle(buf.slice(0, nl))
        buf = buf.slice(nl + 1)
      }
    }
    handle(buf) // trailing line, if any

    if (errMsg) throw new Error(errMsg)
    if (result === undefined) throw new Error('stream ended without a result')
    return result
  }

    return {
      get: (path, opts) => call('GET', path, undefined, opts?.quiet),
      post: (path, body) => call('POST', path, body),
      put: (path, body) => call('PUT', path, body),
      del: (path) => call('DELETE', path),
      streamPost,
    }
  }, [push])
}
